"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var d3 = _interopRequireWildcard(require("d3"));
var _types = require("./types");
var _StackedBarChart = require("./StackedBarChart.styled");
var _useChartDimensions = require("../hooks/useChartDimensions");
var _useChartTooltip = require("../hooks/useChartTooltip");
var _SvgChart = _interopRequireDefault(require("../components/SvgChart"));
var _labelFormatter = require("../utils/label-formatter");
var _useChartData = require("../hooks/useChartData");
var _useAxisOverlap = require("../hooks/useAxisOverlap");
var _useFilter = require("../hooks/useFilter");
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
const BASE_MARGIN = Object.freeze({
  top: 0,
  bottom: 40,
  left: 0,
  right: 0
});
const BAR_GAP = 0.15;
const Y_AXIS_PADDING = 0.1;
const ANIMATION_DURATION = 700;
const StackedBarChart = /*#__PURE__*/(0, _react.memo)(chartProps => {
  var _chart$colors;
  const {
    chart,
    animate,
    chartColors
  } = chartProps;
  const {
    xCol,
    yCol,
    yFormat,
    tooltipCol,
    tooltipFormat,
    fixedScale
  } = chart;
  const {
    showTooltip: tooltipInside,
    showYAxis
  } = chart.componentProps;
  const palette = ((_chart$colors = chart.colors) === null || _chart$colors === void 0 ? void 0 : _chart$colors.schemes.categorical) || chartColors.schemes.categorical;
  const {
    filter,
    filterComponent
  } = (0, _useFilter.useFilter)(chartProps);
  const [animationDone, setAnimationDone] = (0, _react.useState)(!animate);
  const svgRef = _react.default.useRef(null);
  const {
    detectLabelOverlap,
    labelOverlap
  } = (0, _useAxisOverlap.useAxisOverlap)(svgRef, ".x-axis");
  const baseMargin = Object.assign({}, {
    ...BASE_MARGIN
  }, {
    left: showYAxis ? 30 : 0
  });
  let {
    width,
    height,
    margin
  } = (0, _useChartDimensions.useChartDimensions)(svgRef, baseMargin, chart.spanRow, ".x-axis .tick text, .y-axis .tick text");
  const {
    data,
    unfilteredData,
    xLabels,
    xKeys,
    seriesLabels
  } = (0, _useChartData.useChartData)(chart, filter);
  const {
    tooltipRef,
    showTooltip,
    hideTooltip
  } = (0, _useChartTooltip.useChartTooltip)(chart, data);
  const xScale = d3.scaleBand().domain(xKeys).range([0, width]).padding(BAR_GAP);
  const colorPalette = d3.scaleOrdinal().domain(seriesLabels.map(s => s.key)).range(palette);
  const sumData = fixedScale ? unfilteredData : data;
  const yValueSum = xLabels.reduce((prev, xLabel) => {
    const sum = sumData.reduce((obj, d) => {
      const points = d.points.filter(p => p[xCol] === xLabel.key);
      const maxValue = points.length > 0 ? Math.max(...points.map(p => Number(p[yCol]))) : 0;
      const minValue = points.length > 0 ? Math.min(...points.map(p => Number(p[yCol]))) : 0;
      return {
        positive: maxValue >= 0 ? maxValue + obj.positive : obj.positive,
        negative: minValue < 0 ? minValue + obj.negative : obj.negative
      };
    }, {
      positive: 0,
      negative: 0
    });
    return {
      ...prev,
      [xLabel.key]: sum
    };
  }, {});
  const maxYVal = Math.max(...Object.values(yValueSum).map(v => v.positive));
  const minYVal = Math.min(...Object.values(yValueSum).map(v => v.negative));
  const yRange = Math.abs(maxYVal - minYVal);
  const yPadding = yRange * Y_AXIS_PADDING;
  const maxAbsValue = Math.max(Math.abs(maxYVal), Math.abs(minYVal));
  const maxDomain = maxAbsValue + yPadding;
  height *= Math.min(1, maxAbsValue / yRange);

  // Define Y scale
  const yScale = d3.scaleLinear().domain([0, maxDomain]).range([height, 0]);
  let axisPadding = 0;
  if (minYVal < 0) {
    axisPadding = height - yScale(Math.abs(minYVal)) + 18;
  }
  let groupedData = xLabels.map(xLabel => {
    return seriesLabels.reduce((prev, seriesLabel) => {
      const seriesPoints = data.find(d => d.seriesKey === seriesLabel.key).points;
      const xValuePoint = seriesPoints.find(p => p[xCol] === xLabel.key);
      const value = xValuePoint !== null && xValuePoint !== void 0 && xValuePoint[yCol] ? Number(xValuePoint === null || xValuePoint === void 0 ? void 0 : xValuePoint[yCol]) : 0;
      return {
        ...prev,
        ["".concat(seriesLabel.key, "_pos")]: value >= 0 ? value : 0,
        ["".concat(seriesLabel.key, "_neg")]: value < 0 ? value : 0,
        xValue: xLabel.key,
        ["".concat(seriesLabel.key, "_tooltip")]: (xValuePoint === null || xValuePoint === void 0 ? void 0 : xValuePoint[tooltipCol]) || 0
      };
    }, {});
  });
  let stackedData = d3.stack().keys(seriesLabels.map(seriesLabel => "".concat(seriesLabel.key, "_pos")))(groupedData);
  stackedData = stackedData.concat(d3.stack().keys(seriesLabels.map(seriesLabel => "".concat(seriesLabel.key, "_neg")))(groupedData));
  (0, _react.useLayoutEffect)(() => {
    if (svgRef.current === null || height == null || width === null) {
      return;
    }

    // Add x axis
    const xAxisScale = d3.scaleBand().domain(xLabels.map(xVal => xVal.label)).range([0, width]).padding(BAR_GAP);
    const xAxis = d3.axisBottom(xAxisScale).tickSize(labelOverlap ? 5 : 0).tickPadding((labelOverlap ? 2 : 8) + axisPadding);
    d3.select(svgRef.current).select(".x-axis").attr("transform", "translate(0, ".concat(height, ")")).call(xAxis);
    if (showYAxis) {
      let yAxis = d3.axisLeft(yScale).tickSize(0).tickPadding(8).ticks(8).tickFormat(v => _labelFormatter.QuantitativeFormatter[yFormat](v.toString()));
      d3.select(svgRef.current).select(".y-axis").classed("y-axis", true).call(yAxis);
    }
    detectLabelOverlap();
  }, [svgRef, height, width, xLabels, labelOverlap, showYAxis, yScale, yFormat]);
  const handleVisible = (0, _react.useCallback)(() => {
    if (animationDone) return;
    const chart = d3.select(svgRef.current).select(".bar-chart");
    chart.selectAll("rect").data(stackedData.flat()).transition().duration(ANIMATION_DURATION).attr("y", p => p[1] >= 0 ? yScale(p[1]) : yScale(p[0])).attr("height", p => Math.abs(yScale(p[0]) - yScale(p[1])));
    svgRef.current.querySelectorAll(".stacked-bar").forEach(group => group.classList.add("full"));
    window.setTimeout(() => setAnimationDone(true), ANIMATION_DURATION);
  }, [animationDone, stackedData, yScale]);
  let timeout = 0;
  function handleMouseEnter(e) {
    clearTimeout(timeout);
    let el = e.target;
    if (el.tagName === "text") {
      const prev = el.previousElementSibling;
      if (prev) {
        el = prev;
      }
    }
    showTooltip(el);
    svgRef.current.querySelectorAll("rect").forEach(rect => rect.classList.add("semitransparent"));
    svgRef.current.querySelectorAll("rect[data-series=\"".concat(el.dataset.series, "\"]")).forEach(rect => rect.classList.remove("semitransparent"));
  }
  function handleMouseLeave(e) {
    hideTooltip();
    clearTimeout(timeout);
    timeout = window.setTimeout(() => {
      svgRef.current.querySelectorAll("rect").forEach(rect => rect.classList.remove("semitransparent"));
    }, 350);
  }
  return /*#__PURE__*/_react.default.createElement(_SvgChart.default, {
    margin: margin,
    svgRef: svgRef,
    chart: chart,
    tooltipRef: tooltipRef,
    palette: palette,
    keepAggregate: false,
    onVisible: handleVisible,
    filterComponent: filterComponent
  }, /*#__PURE__*/_react.default.createElement(_StackedBarChart.StackedBarChartGroup, {
    className: "bar-chart"
  }, stackedData.map(group => /*#__PURE__*/_react.default.createElement("g", {
    key: group.key,
    className: "stacked-bar ".concat(animate ? "" : "full"),
    fill: colorPalette(rmPolarity(group.key))
  }, group.map(p => /*#__PURE__*/_react.default.createElement("g", {
    key: "".concat(group.key, "-").concat(p.data.xValue)
  }, /*#__PURE__*/_react.default.createElement("rect", {
    x: xScale(p.data.xValue),
    y: !animationDone ? yScale(0) : yScale(p[1] < 0 ? p[0] : p[1]),
    height: !animationDone ? 0 : Math.abs(yScale(p[0]) - yScale(p[1])),
    width: xScale.bandwidth(),
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    "data-x": p.data.xValue,
    "data-series": rmPolarity(group.key),
    "data-color": colorPalette(rmPolarity(group.key))
  }), /*#__PURE__*/_react.default.createElement("text", {
    textAnchor: "middle",
    x: xScale(p.data.xValue),
    y: yScale(p[1] < 0 ? p[0] : p[1]),
    dy: 5 + Math.abs(yScale(p[0]) - yScale(p[1])) / 2,
    dx: xScale.bandwidth() / 2,
    fill: "#ffffff",
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    "data-x": p.data.xValue,
    "data-series": rmPolarity(group.key),
    "data-color": colorPalette(rmPolarity(group.key))
  }, Math.abs(yScale(p[0]) - yScale(p[1])) > 20 && "".concat(_labelFormatter.QuantitativeFormatter[yFormat](p.data[group.key])) + (tooltipInside && tooltipFormat !== _types.ChartQuantitativeFormat.HIDE ? " (".concat(_labelFormatter.QuantitativeFormatter[tooltipFormat](p.data["".concat(rmPolarity(group.key), "_tooltip")]), ")") : ""))))))), /*#__PURE__*/_react.default.createElement("g", {
    className: "x-axis"
  }), /*#__PURE__*/_react.default.createElement("g", {
    className: "y-axis"
  }));
});
var _default = StackedBarChart;
exports.default = _default;
function rmPolarity(group) {
  return group.replace(/(_pos|_neg)$/, "");
}